import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Fire, Bed, Brain, ListChecks, MapPin, Hamburger } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const PSPInfoCardsPage = () => {
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState(null);

  const cards = [
    {
      id: 'burnout',
      emoji: '🔥',
      title: 'Burn Bright, Don\'t Burn Out',
      subtitle: 'Surviving the scene without losing yourself',
      icon: Fire,
      color: 'from-orange-500 to-red-500',
      content: {
        intro: "The scene isn't the problem — losing yourself is. When engagement becomes default rather than choice, when you stop checking in with your motivations, burnout sneaks up fast.",
        sections: [
          {
            title: '⚠️ Warning Signs of Burnout',
            items: [
              'Sessions feel mechanical — the spark is gone',
              'Using more but feeling less',
              'You dread the crash before you even start',
              'You feel alone even in groups',
              'You don\'t enjoy anything sober anymore'
            ]
          },
          {
            title: '1. Come Back to You',
            text: 'Don\'t disappear from yourself to belong in it.',
            items: [
              'The scene is a choice, not a default',
              'Maintain your identity outside the party',
              'Check in with why you\'re doing this'
            ]
          },
          {
            title: '2. Be Picky with Who Gets Access',
            text: 'Not everyone is good for your energy. Ask yourself:',
            items: [
              'Who makes me feel grounded afterward?',
              'Who checks in when the vibe changes?',
              'Who knows the real me, not just the party me?',
              'Build a small circle you trust — real connection is rare'
            ]
          },
          {
            title: '3. Plan for the Crash',
            text: 'The crash is part of the cycle. Ignoring it makes it harder.',
            items: [
              'Set up a post-session kit: water, snacks, vitamins, comfy clothes',
              'Ask one person to check in on you after',
              'Leave yourself a kind note or voice memo for later',
              'Plan your "after" BEFORE you start: Eat → Shower → Sleep'
            ]
          },
          {
            title: '4. Rituals Outside the High',
            text: 'If all your rituals are tied to being high, your nervous system never learns to feel good sober.',
            items: [
              'One playlist for sober-you',
              'A daily body check-in',
              'A safe space to be real (journal, quiet room, private chat)',
              'One small habit that\'s just for you'
            ]
          },
          {
            title: '5. You Are More Than Your Party Version',
            items: [
              'You\'re not just the role you play in the session',
              'You had a life before the scene and a future after it',
              'That empty feeling after the high? That\'s not weakness — it\'s the part of you that still wants wholeness',
              'You\'re allowed to step back without disappearing'
            ]
          },
          {
            title: '6. Return Ritual',
            text: 'When the music stops, check in — don\'t shame yourself.',
            items: [
              'Shower',
              'Warm drink or tea',
              'Fresh clothes',
              'Step outside for 2 minutes',
              'Text one safe person',
              'One sober playlist for grounding'
            ]
          }
        ],
        footer: 'Burn bright — but with intention. Stay alive. Stay grounded. Stay you.'
      }
    },
    {
      id: 'plan-play',
      emoji: '📋',
      title: 'Plan & Play',
      subtitle: 'Prep before you party',
      icon: MapPin,
      color: 'from-blue-500 to-cyan-500',
      content: {
        intro: "Before anything, ask yourself: Am I in the right frame of mind to play or go out? Be honest.",
        sections: [
          {
            title: 'PrEP the Session',
            items: [
              'Estimate how long the hook-up might last',
              'Video call new guys to verify profiles (catfish risk is real)',
              'Agree an end time with your host and stick to it',
              'Bring some cash to chip in for supplies',
              'Have an exit strategy in case you need to leave fast',
              'Tell someone you trust where you\'re going',
              'Carry naloxone',
              'Add an emergency contact to your phone\'s lock screen',
              'Take a photo of the street when you arrive',
              'Don\'t cruise or hunt for new meets when high — you\'re more vulnerable',
              'Keep your stuff zipped up and on you'
            ]
          },
          {
            title: 'Pack Smart',
            items: [
              'Fully charge your phone + bring charger, cable, power bank',
              'Backup cash/card (keep separate from supply money)',
              'Meds? Take them, bring extra just in case',
              'Mini toiletry bag: tissues, wipes',
              'Condoms, lube, gloves (fisting), douche kit, clean slamming kit',
              'Bottled water, energy bar, mints/gum',
              'Feed your pets before you leave'
            ]
          }
        ],
        footer: 'Plan ahead. Play smart. Come home safe.'
      }
    },
    {
      id: 'sleep-deficit',
      emoji: '😴',
      title: 'The Sleep Deficit',
      subtitle: 'How skipping sleep messes with your brain',
      icon: Bed,
      color: 'from-indigo-500 to-purple-500',
      content: {
        intro: "In PnP, sleep is usually the first thing to go. Long sessions. Tina. Group energy. Before you know it, you've been up for a day, two, or more. This isn't just about being tired — no sleep changes how your brain works.",
        sections: [
          {
            title: 'What Sleep Does for Your Brain',
            items: [
              'Cleans itself',
              'Processes feelings',
              'Stores memories',
              'Resets your nervous system',
              'No sleep = the system starts breaking down fast'
            ]
          },
          {
            title: '24 Hours Awake',
            items: [
              'Slower reactions',
              'Bad judgment',
              'Hard to focus',
              'More stress',
              '(Your brain is functioning like you\'re drunk)'
            ]
          },
          {
            title: '36-48 Hours Awake',
            items: [
              'The logic part of your brain checks out',
              'Emotions take over',
              'Big mood swings',
              'Memory gets messy',
              'You react before you think'
            ]
          },
          {
            title: '48+ Hours Awake',
            danger: true,
            items: [
              'Your brain starts doing micro "blackouts"',
              'Hallucinations can show up',
              'Paranoia ramps up',
              'Confusion, looping thoughts',
              'Reality gets unreliable'
            ]
          },
          {
            title: 'How This Shows Up in PnP',
            text: 'Sleep loss + drugs + intense vibes = things can flip quick.',
            items: [
              'Big emotional swings (crying, snapping, spiraling)',
              'Paranoia (thinking people are against you)',
              'Hallucinations (seeing/hearing things)',
              'Misreading vibes (thinking danger when it\'s not)',
              'Memory gaps'
            ]
          },
          {
            title: 'PSP Harm Reduction — Sleep',
            items: [
              'Set a session limit — know when you\'re stopping',
              'Rest every 24 hours (even 20-60 min helps)',
              'Create sleep vibes: eye mask, earplugs, low light, calm sounds',
              'Build downtime before jumping back into chats or sessions',
              'If someone is paranoid, emotional, or hallucinating — they probably need sleep, not more drugs'
            ]
          }
        ],
        footer: 'Sleep isn\'t killing the vibe. Sleep is what keeps the vibe from turning into chaos.'
      }
    },
    {
      id: 'eat-sleep-survive',
      emoji: '🍌',
      title: 'Eat, Sleep, Survive',
      subtitle: 'The basics that keep you grounded',
      icon: Hamburger,
      color: 'from-green-500 to-emerald-500',
      content: {
        intro: "When you're in PnP, your body is under big stress. Not eating. Not sleeping. Ghosting real life. It might feel fine in the moment, but this is what burns people out fast. This is how paranoia, crashes, and mental health spirals start.",
        sections: [
          {
            title: '😴 Sleep Is Not Optional',
            text: 'Tina makes you feel awake while your brain is losing it behind the scenes.',
            items: [
              'Paranoia goes up',
              'Reality gets blurry',
              'You forget stuff, misread people, lose track of time',
              'Brain gets stuck in loops (overthinking, spiraling)',
              'Hallucinations hit harder and last longer',
              'Anxiety and comedowns feel way worse'
            ]
          },
          {
            title: 'Sleep Harm Reduction',
            items: [
              'Set a limit for your session (know when you\'re stopping)',
              'Try to rest every 24 hours (even 20-30 min helps)',
              'Wind down on purpose (lay down, close eyes, low lights, no screens)',
              'Sleep is medicine — no pill or vitamin replaces real rest'
            ]
          },
          {
            title: '🍎 Food = Fuel',
            text: 'You might not feel hungry, but your body needs food to regulate mood, protect your brain, and help with comedowns.',
            items: [
              'No food = harder crash',
              'Big mood swings from low blood sugar',
              'Brain fog, bad decisions, no focus',
              'Your body starts breaking itself down',
              'More paranoia and anxiety'
            ]
          },
          {
            title: 'Eating Harm Reduction',
            items: [
              'Eat before you start (even a smoothie is something)',
              'Keep easy snacks nearby (bananas, yogurt, nuts, protein bars, soup)',
              'Set food reminders every 3-4 hours'
            ]
          },
          {
            title: '🏠 Daily Life Still Matters',
            text: 'PnP can disconnect you from normal life. The longer you check out, the harder it is to come back.',
            items: [
              'Keep tiny routines alive (brush teeth, shower, open blinds)',
              'Move your body (stretch, walk, dance for 2 minutes)',
              'Stay connected (one text = still human)',
              'Plan your "after": Eat → Shower → Sleep → Reset'
            ]
          },
          {
            title: '🦷 Teeth Check: Avoiding "Meth Mouth"',
            text: 'It happens when you stop brushing and let residue sit on your teeth.',
            items: [
              'Brush at least once a day (twice is better)',
              'Rinse your mouth after smoking',
              'Drink water (dry mouth = more tooth damage)',
              'Sugar-free gum helps with saliva',
              'Your smile deserves care too'
            ]
          }
        ],
        footer: 'You don\'t need to fix your whole life. Just don\'t disappear from it.'
      }
    },
    {
      id: 'psychosis',
      emoji: '🧠',
      title: 'Psychosis Guide',
      subtitle: 'When the mind loses touch with reality',
      icon: Brain,
      color: 'from-red-500 to-pink-500',
      content: {
        intro: "Psychosis is when someone loses touch with reality. It can happen while high, during the comedown, or even days later — especially after sleep deprivation. It can happen to anyone if the body and brain are pushed too far.",
        sections: [
          {
            title: 'Drugs Most Linked to Psychosis',
            items: [
              'Methamphetamine (and other amphetamines)',
              'Cocaine',
              'MDMA (especially in high or frequent doses)',
              'GHB (in rare cases, especially during withdrawal)',
              'Ketamine',
              'Synthetic cannabinoids',
              'LSD or mushrooms'
            ]
          },
          {
            title: 'What Makes It Worse',
            danger: true,
            items: [
              'Mixing multiple substances',
              'Re-dosing again and again',
              'No sleep for 2+ nights',
              'Not eating',
              'Not hydrating',
              'Using alone',
              'Already feeling depressed or unstable before using'
            ]
          },
          {
            title: '⚠️ Early Warning Signs',
            danger: true,
            items: [
              'Obsessively checking windows, phones, cameras',
              'Thinking people are talking about you',
              'Hearing voices',
              'Getting messages from music or TV',
              'Talking in a scattered or repetitive way',
              'Not sleeping for 2+ nights',
              'Extreme fear or agitation'
            ]
          },
          {
            title: 'Before — Harm Reduction',
            items: [
              'Sleep the night before',
              'Eat real food (protein + carbs)',
              'Hydrate early',
              'Check your emotional state — if grieving, anxious, or unstable, reconsider',
              'Set limits before you start',
              'Don\'t party completely alone'
            ]
          },
          {
            title: 'During — Stay Grounded',
            items: [
              'Avoid stacking substances',
              'Take breaks — sit down, breathe, stretch',
              'Drink water, eat small snacks',
              'Check in with yourself honestly',
              'Watch the clock — risk rises fast after 12-24 hours awake'
            ]
          },
          {
            title: 'After — Gentle Recovery',
            items: [
              'Sleep — even if it takes effort',
              'Eat nourishing food',
              'Hydrate',
              'Don\'t immediately re-dose to avoid the crash (usually makes it worse)',
              'Stay connected — isolation amplifies paranoia',
              'Grounding activities: shower, journaling, calm music, breathing',
              'Treat your brain gently — it is inflamed and depleted'
            ]
          },
          {
            title: '🚨 If Someone Is in Psychosis',
            danger: true,
            items: [
              'Stay calm',
              'Don\'t argue with their beliefs',
              'Lower stimulation (lights, music, noise)',
              'Speak slowly and gently',
              'Offer water or light food',
              'Stay nearby if safe',
              'If aggressive, extremely disoriented, or a danger — get medical help',
              'Mental health crisis support exists for a reason. Safety first.'
            ]
          }
        ],
        footer: 'No one plans to lose their mind. But in party culture, long nights + heavy use + silence about mental health makes it easier to happen. Your mental health is not separate from your party life. Look out for each other.'
      }
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedCard ? setSelectedCard(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">📋</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedCard ? cards.find(c => c.id === selectedCard)?.title : 'PSP Info Cards'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedCard ? (
          <>
            {/* Hero */}
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold mb-3">PSP Info Cards</h2>
              <p className="text-white/90 leading-relaxed">
                Quick, direct guides for real situations. Simplified language. No unnecessary complexity. Because harm reduction only works if people can access it. 💜
              </p>
            </div>

            {/* Cards */}
            <div className="space-y-4">
              {cards.map((card) => {
                const Icon = card.icon;
                return (
                  <button
                    key={card.id}
                    onClick={() => setSelectedCard(card.id)}
                    data-testid={`card-${card.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-start">
                      <div className="text-5xl mr-4 emoji-bounce">{card.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                          {card.title}
                        </h3>
                        <p className="text-sm text-[rgb(var(--text-secondary))]">
                          {card.subtitle}
                        </p>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-8 p-4 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
              <p className="text-sm text-[rgb(var(--text-secondary))]">
                Built by community, for community. Real voices. Real experiences. Simplified for when you need it most.
              </p>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {cards.find(c => c.id === selectedCard)?.content && (() => {
              const card = cards.find(c => c.id === selectedCard);
              const content = card.content;
              
              return (
                <>
                  {/* Intro */}
                  <div className={`p-6 bg-gradient-to-br ${card.color} rounded-3xl text-white`}>
                    <p className="leading-relaxed">{content.intro}</p>
                  </div>

                  {/* Sections */}
                  {content.sections.map((section, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-2xl p-5 border ${section.danger ? 'bg-[rgb(var(--danger))]/10 border-[rgb(var(--danger))]/30' : 'bg-[rgb(var(--surface))] border-white/5'}`}
                    >
                      <h3 className={`text-lg font-bold mb-3 ${section.danger ? 'text-[rgb(var(--danger))]' : 'text-[rgb(var(--text-primary))]'}`}>
                        {section.title}
                      </h3>
                      {section.text && (
                        <p className="text-sm text-[rgb(var(--text-secondary))] mb-3">{section.text}</p>
                      )}
                      {section.items && (
                        <ul className="space-y-2">
                          {section.items.map((item, i) => (
                            <li key={i} className="text-sm text-[rgb(var(--text-secondary))] flex items-start">
                              <span className={`mr-2 ${section.danger ? 'text-[rgb(var(--danger))]' : 'text-[rgb(var(--primary))]'}`}>•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  {/* Footer */}
                  <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
                    <p className="text-[rgb(var(--text-primary))] font-semibold">{content.footer}</p>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default PSPInfoCardsPage;
